<template>
  <BaseWidget
    class="data-table"
    :title="content.title"
    :widgetId="content.id"
    :edit="edit"
    :content="content"
    :isWidgetLoading="isWidgetLoading"
    :getWidgetReport="getWidgetReport"
  >
    <BaseTable
      :data="data"
      :headers="headers"
      :rowsPerPage="20"
      :isWidgetLoading="isWidgetLoading"
      :content="content"
      :summary-row="summaryRow"
      :footer-props="footerProps"
      isTableWidget
      hide-footer
      comparison-widget
      hide-icons
    />
  </BaseWidget>
</template>

<script lang="ts">
import Vue from "vue";
import { DataOptions } from "vuetify";
import { ThemeOptions } from "vuetify/types/services/theme";
import { mapGetters } from "vuex";

import ProjectWidgetApi from "@/classes/ProjectWidgetApi";
import BaseTable from "@/components/tables/BaseTable.vue";
import BaseWidget from "@/components/widgets/explore/BaseWidget.vue";
import csvExport, { buildFileName, buildHeaderRows } from "@/helpers/csvExport";
import Lang from "@/lang";
import summaryTableMetrics from "@/settings/table/summaryTableMetrics";
import { TableDataRow } from "@/types/charts";
import { PlanInputDictionary } from "@/types/common";
import { ContentWidget } from "@/types/layouts";
import { Project } from "@/types/project";

type TableHeader = {
  text: string;
  value: string;
  width?: string;
  sortable?: boolean;
};

type ComparisonRow = Record<string, string | number | null>;

function slugify(str: string): string {
  return (str || "")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

const METRIC_MAP: Record<string, { title?: string; formatter?: (n: number) => string | number }> = {};
(summaryTableMetrics as PlanInputDictionary[]).forEach((d) => {
  if (d && d.value) METRIC_MAP[d.value] = { title: d.title, formatter: d.formatter };
});

const BASE_METRICS = ["channel_budget", "impressions", "cpm"];

const KPI_COST_METRIC: Record<string, string> = {};
const _kpiDuplicates: Record<string, string[]> = {};
(summaryTableMetrics as PlanInputDictionary[]).forEach((d) => {
  if (!d?.kpiTrigger || !d?.value) return;
  const kpi = String(d.kpiTrigger);
  const metric = String(d.value);
  if (!(kpi in KPI_COST_METRIC)) {
    KPI_COST_METRIC[kpi] = metric;
    _kpiDuplicates[kpi] = [metric];
  } else {
    _kpiDuplicates[kpi].push(metric);
  }
});

const norm = (s: string) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");

const KPI_VOLUME_METRIC: Record<string, string> = {};
(summaryTableMetrics as PlanInputDictionary[]).forEach((d) => {
  if (d?.kpiVolumeTrigger && d?.value) KPI_VOLUME_METRIC[norm(String(d.kpiVolumeTrigger))] = String(d.value);
});

export default Vue.extend({
  name: "ComparisonTableWidget",

  props: {
    content: Object as () => ContentWidget,
    edit: Boolean,
  },

  components: { BaseWidget, BaseTable },

  async beforeMount() {
    this.theme = this.$vuetify.theme;
  },

  data() {
    return {
      Lang,
      options: {} as DataOptions,
      isWidgetLoading: false,
      data: [] as TableDataRow[],
      headerLabels: [] as TableHeader[],
      theme: {} as ThemeOptions,
      summaryRow: {} as Record<string, unknown>,
      rowsPerPage: null as null | number,
      footerProps: {
        showCurrentPage: true,
        disableItemsPerPage: true,
        disablePagination: false,
      },
    };
  },

  computed: {
    ...mapGetters("projectOutput", ["projectFiltered"]),

    headers(): TableHeader[] {
      return this.headerLabels;
    },
  },

  watch: {
    projectFiltered: {
      immediate: true,
      handler() {
        this.rebuild();
      },
    },
  },

  methods: {
    async rebuild() {
      this.isWidgetLoading = true;
      this.buildHeadersFromProject();
      await this.loadDataFromApi();
      this.isWidgetLoading = false;
    },

    buildHeadersFromProject(): void {
      const headers: TableHeader[] = [];
      headers.push({ text: "", value: "metric", sortable: false, width: "180px" });

      const sortedNames = this.extractScenarioNames()
        .slice()
        .sort((a, b) => a.localeCompare(b));
      for (const name of sortedNames) {
        const key = `scenario_${slugify(name)}`;
        headers.push({ text: name, value: key, sortable: false, width: "150px" });
      }
      this.headerLabels = headers;
    },

    extractScenarioNames(): string[] {
      const proj = this.projectFiltered as Project;
      if (!proj || !Array.isArray(proj.business_goals)) return [];
      const set = new Set<string>();
      for (const bg of proj.business_goals || []) {
        for (const pl of bg.plans || []) {
          for (const sc of pl.scenarios || []) {
            const name = (sc?.name || "").toString().trim();
            if (name) set.add(name);
          }
        }
      }
      return Array.from(set).sort((a, b) => a.localeCompare(b));
    },

    collectKpisFromProject(): string[] {
      const proj = this.projectFiltered;
      const out: string[] = [];
      const seen = new Set<string>();
      if (!proj || !Array.isArray(proj.business_goals)) return out;

      for (const bg of proj.business_goals || []) {
        for (const pl of bg.plans || []) {
          for (const sc of pl.scenarios || []) {
            const scKpi = sc?.campaign_kpi ?? sc?.campaignKpi ?? null;
            if (scKpi != null) {
              const s = String(scKpi).trim();
              if (s && !seen.has(s)) {
                seen.add(s);
                out.push(s);
              }
            }
            const rows = sc?.output?.data || [];
            for (const r of rows) {
              const k = r?.campaign_kpi ?? r?.campaignKpi ?? null;
              if (k != null) {
                const s = String(k).trim();
                if (s && !seen.has(s)) {
                  seen.add(s);
                  out.push(s);
                }
              }
            }
          }
        }
      }
      return out;
    },

    buildMetricsOrderWithPairs(kpis: string[]) {
      const added = new Set<string>();
      const kpiPairs: Array<{ kpi: string; volume: string | null; cost: string | null }> = [];

      for (const m of BASE_METRICS) if (!added.has(m)) added.add(m);

      const kpiLower = kpis.map((k) => (k || "").toLowerCase().trim());
      const hasROAS = kpiLower.includes("roas");
      const hasPurchase = kpiLower.includes("purchase") || kpiLower.includes("purchases");

      for (const kpi of kpis) {
        let volume: string | null = KPI_VOLUME_METRIC[norm(kpi)] ?? null;
        let cost: string | null = KPI_COST_METRIC[kpi] || null;

        if ((kpi || "").toLowerCase().trim() === "roas") {
          volume = "roas";
          cost = null;
        }

        const pair = { kpi, volume, cost };
        kpiPairs.push(pair);

        if (volume && !added.has(volume)) added.add(volume);
        if (cost && cost !== volume && !added.has(cost)) added.add(cost);
      }

      if (!hasROAS && hasPurchase) {
        kpiPairs.push({ kpi: "ROAS", volume: "roas", cost: null });
      }

      return kpiPairs;
    },
    async loadDataFromApi(): Promise<void> {
      if (!this.projectFiltered) {
        this.data = [];
        return;
      }
      const api = new ProjectWidgetApi();

      const kpis = this.collectKpisFromProject();
      const kpiPairs = this.buildMetricsOrderWithPairs(kpis);

      const rawAll = await api.getScenarioMetricsAll(this.projectFiltered);

      const byMetricKey = new Map<string, Record<string, string | number>>();
      for (const r of rawAll || []) {
        if (r && typeof r.metric === "string") byMetricKey.set(r.metric, r);
      }
      const outRows: ComparisonRow[] = [];
      const normKey = (k: string) => (k || "").toLowerCase().replace(/[^a-z0-9]/g, "");
      const pickExistingKey = (...candidates: string[]): string | null => {
        for (const c of candidates) if (byMetricKey.has(c)) return c;
        const wanted = new Set(candidates.map(normKey));
        for (const k of byMetricKey.keys()) if (wanted.has(normKey(k))) return k;
        return null;
      };
      const consumed = new Set<string>();

      const addMetricRow = (metricKey: string, titleFallback?: string) => {
        const key = pickExistingKey(metricKey, metricKey.endsWith("s") ? metricKey.slice(0, -1) : metricKey + "s");
        if (!key || consumed.has(normKey(key))) return;
        const row = { ...byMetricKey.get(key) };
        const fmt = METRIC_MAP[key]?.formatter ?? METRIC_MAP[metricKey]?.formatter;
        if (typeof fmt === "function") {
          Object.keys(row).forEach((k) => {
            if (k === "metric") return;
            const v = row[k];
            const n = typeof v === "number" && isFinite(v) ? v : Number(v);
            if (isFinite(n)) row[k] = fmt(n);
          });
        }
        row.metric = METRIC_MAP[key]?.title || METRIC_MAP[metricKey]?.title || titleFallback || key;
        outRows.push(row);
        consumed.add(normKey(key));
      };

      addMetricRow("channel_budget", "Budget");
      addMetricRow("impressions", "Impressions");
      addMetricRow("cpm", "CPM");

      for (const { kpi, volume, cost } of kpiPairs) {
        if (volume && byMetricKey.has(volume) && !consumed.has(normKey(volume))) {
          const src = byMetricKey.get(volume);
          const row = { ...src };
          const fmt = METRIC_MAP[volume]?.formatter;
          if (typeof fmt === "function") {
            Object.keys(row).forEach((k) => {
              if (k === "metric") return;
              const v = row[k];
              const n = typeof v === "number" && isFinite(v) ? v : Number(v);
              if (isFinite(n)) row[k] = fmt(n);
            });
          }
          row.metric = METRIC_MAP[volume]?.title || kpi;
          outRows.push(row);
          consumed.add(normKey(volume));
        } else if (!volume) {
          const empty: Record<string, string | number | null> = { metric: kpi };
          this.headers
            .map((h) => h.value)
            .filter((v) => v !== "metric")
            .forEach((col) => (empty[col] = null));
          outRows.push(empty);
        }

        if (cost && normKey(cost) !== "roas" && byMetricKey.has(cost) && !consumed.has(normKey(cost))) {
          const src = byMetricKey.get(cost);
          const row = { ...src };
          const fmt = METRIC_MAP[cost]?.formatter;
          if (typeof fmt === "function") {
            Object.keys(row).forEach((k) => {
              if (k === "metric") return;
              const v = row[k];
              const n = typeof v === "number" && isFinite(v) ? v : Number(v);
              if (isFinite(n)) row[k] = fmt(n);
            });
          }
          row.metric = METRIC_MAP[cost]?.title || cost;
          outRows.push(row);
          consumed.add(normKey(cost));
        }
      }
      this.data = outRows as TableDataRow[];
    },

    buildExportMatrix(): unknown[][] {
      const out: unknown[][] = [];

      out.push(this.headers.map((h) => (h.value === "metric" ? "Metric" : String(h.text ?? ""))));

      for (const row of this.data) {
        const csvRow: unknown[] = [];
        this.headers.forEach((h) => {
          const val = (row as Record<string, unknown>)[h.value];
          csvRow.push(val ?? "");
        });
        out.push(csvRow);
      }

      return out;
    },

    getWidgetReport(): void {
      const title = this.content?.title || "Comparison Table";
      const csvContent: unknown[][] = [];

      csvContent.push(...buildHeaderRows(title));
      csvContent.push([]);
      csvContent.push(...this.buildExportMatrix());

      csvExport(csvContent, buildFileName(title));
    },
  },
});
</script>
